import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type {
  CheckoutBridgeService,
  CheckoutBridgeServiceOption,
} from '@/scopes/checkout/domain/types/services/bridge'
import type { APIBookingDraft, APIBookingDraftRoom } from '@/scopes/checkout/infrastructure/api/types'
import type {
  APIBookingDraftBridgeService,
  APIBookingDraftBridgeServiceOption,
} from '@/scopes/checkout/infrastructure/api/types/services/bridge'
import { values } from 'ramda'
import { type CheckoutParticipantAgeCategory, ServiceMapper, ServiceType } from '../../../domain/types'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceOptionMapper?: ServiceOptionMapper
  serviceType: ServiceType
}

export function createBridgeServiceMapper(dependencies: Dependencies): ServiceMapper<CheckoutBridgeService> {
  const {
    serviceName,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceType,
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft): CheckoutBridgeService | null {
    const service = bookingDraft.services.find(
      ({ aName }) => aName === serviceName,
    ) as unknown as APIBookingDraftBridgeService

    if (!service)
      return null

    const { aName, options } = service

    if (!options || options.length === 0)
      return null

    const participants = bookingDraft.participants.reduce<CheckoutBridgeService['participants']>(
      (acc, participant) => {
        const participantOptions = options.filter(o =>
          o.appliesToParticipants.includes(participant.id),
        )

        if (participantOptions.length === 0)
          return acc

        const selectedOptions = participantOptions.filter(
          o => o.isSelected && o.assignedParticipants.includes(participant.id),
        )

        return {
          ...acc,
          [participant.id.toString()]: {
            id: participant.id.toString(),
            name: participant.name || participant.alternativeName?.toLowerCase() || '',
            roomId: participant.roomId.toString(),
            birthdate: participant.birthdate ?? null,
            ageCategory: participant.ageCategory as CheckoutParticipantAgeCategory,
            selectedValues: selectedOptions.map(o => o.id),
            availableOptions: participantOptions.map(o => o.id),
          },
        }
      },
      {},
    )

    const anyParticipantHasSelectedValue = values(participants).some(p => p.selectedValues.length > 0)

    return {
      id: aName,
      type: ServiceType.Bridge,
      isSelected: anyParticipantHasSelectedValue,
      rooms: bookingDraft.rooms?.map(mapRoom) ?? [],
      participants,
      options: options.reduce<Record<string, CheckoutBridgeServiceOption>>(
        (acc, option) => ({
          ...acc,
          [option.id]: mapOption(option),
        }),
        {},
      ),
    }
  }

  function mapOption(
    option: APIBookingDraftBridgeServiceOption,
  ): CheckoutBridgeServiceOption {
    const priceAssignments = mapPriceAssignments(option)

    return {
      id: option.id,
      code: option.code,
      label: option.displayText,
      price: option.price,
      startDate: option.startDate,
      endDate: option.endDate,
      isSelected: option.isSelected,
      isIncluded: option.isIncluded,
      ...(priceAssignments && { priceAssignments }),
    }
  }

  function mapPriceAssignments(
    option: APIBookingDraftBridgeServiceOption,
  ): CheckoutBridgeServiceOption['priceAssignments'] | undefined {
    const prices = (option.prices ?? []).filter(p => p.participantIds.length > 0)
    if (prices.length === 0)
      return undefined

    return prices.map(({ price, participantIds }) => ({
      price,
      participantIds: participantIds.map(id => id.toString()),
    }))
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map(pid => pid.toString()),
    }
  }

  function mapSelection(service: CheckoutBridgeService) {
    const { options, participants } = service

    // Group options by unique date range
    const dateRangeMap = new Map<string, CheckoutBridgeServiceOption[]>()
    Object.values(options).forEach((option) => {
      const key = `${option.startDate}||${option.endDate}`
      if (!dateRangeMap.has(key))
        dateRangeMap.set(key, [])
      dateRangeMap.get(key)!.push(option)
    })

    const result: Array<{ participantId: string, value: string | null, startDate: string, endDate: string }> = []

    dateRangeMap.forEach((dateOptions) => {
      const { startDate, endDate } = dateOptions[0]
      const dateOptionIds = new Set(dateOptions.map(o => o.id))

      Object.values(participants).forEach((participant) => {
        const hasAccess = participant.availableOptions.some(id => dateOptionIds.has(id))
        if (!hasAccess)
          return

        const selectedOptionId = participant.selectedValues.find(id => dateOptionIds.has(id))
        const selectedOption = selectedOptionId ? options[selectedOptionId] : null

        result.push({
          participantId: participant.id,
          value: selectedOption ? selectedOption.code : null,
          startDate,
          endDate,
        })
      })
    })

    return result
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutBridgeService, step: number): ImpressionViewAncillariesItem[] {
    const itemsById = new Map<string, SelectedItemInput>()

    Object.entries(service.participants).forEach(([participantId, participant]) => {
      participant.selectedValues.forEach((optionId) => {
        const option = service.options[optionId]
        if (!option)
          return

        itemsById.set(option.code, {
          label: option.label,
          value: option.code,
          price: option.priceAssignments?.find(pa => pa.participantIds.includes(participantId))?.price ?? option.price,
        })
      })
    })

    return mapDefaultSelectedItemToGA4(Array.from(itemsById.values()), {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
